import React from 'react';
import PropTypes from 'prop-types';
import { Columns, LayoutGrid } from 'lucide-react';
import { cn } from '../../lib/utils';
import { Button } from '../ui/button';
import { GRID_CONFIG } from '../../constants/tileConfig';

/**
 * TileGrid - Container component that manages the grid layout
 * Handles the 10-unit grid system with deck area and tile area
 */
function TileGrid({
  layoutMode = 'default',
  onLayoutModeChange,
  deckAreaContent,
  children,
  className,
}) {
  const layout = GRID_CONFIG.layouts[layoutMode] || GRID_CONFIG.layouts.default;

  // Toggle between default and expanded modes
  const cycleLayoutMode = () => {
    const nextMode = layoutMode === 'default' ? 'expanded' : 'default';
    onLayoutModeChange(nextMode);
  };

  // Get layout icon based on current mode
  const getLayoutIcon = () => {
    return layoutMode === 'expanded' 
      ? <Columns className="h-4 w-4" />
      : <LayoutGrid className="h-4 w-4" />;
  };

  // Get the layout mode label
  const getLayoutLabel = () => {
    return layoutMode === 'expanded' ? 'Expanded View' : 'Default View';
  };

  return (
    <div
      className={cn(
        'tile-grid',
        'grid gap-3 h-full',
        'transition-all duration-300 ease-in-out',
        className
      )}
      style={{
        gridTemplateColumns: `repeat(${GRID_CONFIG.totalColumns}, 1fr)`,
      }}
    >
      {/* Deck Area */}
      <div
        className={cn(
          'deck-area',
          'flex flex-col min-h-0',
          'transition-all duration-300 ease-in-out'
        )}
        style={{
          gridColumn: `span ${layout.deckArea}`,
        }}
      >
        {deckAreaContent}
      </div>

      {/* Tile Area (Sidebar) */}
      <div
        className={cn(
          'tile-area',
          'flex flex-col gap-3 min-h-0',
          'transition-all duration-300 ease-in-out'
        )}
        style={{
          gridColumn: `span ${layout.tileArea}`,
        }}
      >
        {/* Layout Toggle Header */}
        <div
          className={cn(
            'tile-area-header',
            'flex items-center justify-between',
            'px-3 py-2',
            'bg-gradient-to-r from-[#3c618a] to-[#7a67ba]',
            'text-white rounded-lg',
            'flex-shrink-0'
          )}
        >
          <span className="font-semibold text-sm">🔍 Search Cards</span>
          <Button
            variant="ghost"
            size="sm"
            className="h-7 p-1 text-white hover:bg-white/20"
            onClick={cycleLayoutMode}
            title={`Current: ${getLayoutLabel()}. Click to change.`}
          >
            {getLayoutIcon()}
            <span className="ml-1 text-xs">{layoutMode === 'expanded' ? 'Collapse' : 'Expand'}</span>
          </Button>
        </div>

        {/* Tiles Container */}
        <div
          className={cn(
            'tiles-container',
            'flex flex-col gap-3',
            'flex-1 min-h-0 overflow-y-auto'
          )}
        >
          {children}
        </div>
      </div>
    </div>
  );
}

TileGrid.propTypes = {
  layoutMode: PropTypes.oneOf(['default', 'expanded']),
  onLayoutModeChange: PropTypes.func.isRequired,
  deckAreaContent: PropTypes.node,
  children: PropTypes.node,
  className: PropTypes.string,
};

export default TileGrid;
